import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Autocomplete from "@/components/Autocomplete";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["PCS", "SETS"];

export default function OrderEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState({ name: "", city: "", phone: "", agent: "", agent_phone: "" });
  const [items, setItems] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => {
      const o = r.data;
      setOrder(o);
      setCustomer({
        name: o.customer.name || "",
        city: o.customer.city || "",
        phone: o.customer.phone || "",
        agent: o.customer.agent || "",
        agent_phone: o.customer.agent_phone || "",
      });
      setItems((o.items || []).map((it) => ({
        design_number: it.design_number,
        quantity: it.quantity,
        unit: it.unit || "PCS",
        rate: it.rate,
        photo_path: it.photo_path || "",
      })));
      setRemarks(o.remarks || "");
    }).catch(() => navigate("/orders"));
  }, [id, navigate]);

  const fetchCustomers = async (q) => {
    const { data } = await api.get("/customers", { params: q ? { q } : {} });
    return (data || []).map((c) => ({ label: c.name, meta: [c.phone, c.city, c.agent].filter(Boolean).join(" · "), raw: c }));
  };
  const fetchAgents = async (q) => {
    const { data } = await api.get("/agents", { params: q ? { q } : {} });
    return (data || []).map((a) => ({ label: a.name, meta: a.phone || "", raw: a }));
  };

  const updateItem = (i, key, value) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [key]: value };
    setItems(copy);
  };
  const addItem = () => setItems([...items, { design_number: "", quantity: 1, unit: "PCS", rate: 0, photo_path: "" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.rate) || 0), 0);
  const gstAmt = Math.round(subtotal * 0.05 * 100) / 100;
  const grand = Math.round((subtotal + gstAmt) * 100) / 100;

  const save = async () => {
    if (!customer.name || !customer.city || !customer.phone || !customer.agent) {
      toast.error("Customer name, city, phone, agent are required"); return;
    }
    if (items.length === 0) { toast.error("Add at least one item"); return; }
    for (const it of items) {
      if (!it.design_number || !it.quantity || it.rate === "" || it.rate === undefined) {
        toast.error("Every item needs design number, qty and rate"); return;
      }
    }
    setSaving(true);
    try {
      await api.patch(`/orders/${id}`, {
        customer,
        items: items.map((it) => ({
          design_number: it.design_number.trim(),
          quantity: parseInt(it.quantity, 10),
          unit: (it.unit || "PCS").toUpperCase(),
          rate: parseFloat(it.rate),
          photo_path: it.photo_path || "",
        })),
        remarks,
      });
      toast.success("Order updated");
      navigate(`/orders/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!order) return <div className="brut-card p-6">LOADING…</div>;

  return (
    <div className="max-w-4xl mx-auto" data-testid="order-edit-page">
      <button onClick={() => navigate(-1)} className="brut-btn brut-btn-secondary !py-2 text-xs mb-4" data-testid="back-btn">
        <ArrowLeft size={14} strokeWidth={3} /> BACK
      </button>

      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#E63946] font-bold mb-2">── EDIT ORDER</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter">{order.order_no}</h1>
        <div className="text-xs text-[#666] mt-1 uppercase tracking-wider">Changes will be logged in the action log.</div>
      </div>

      <div className="brut-card p-6 space-y-4 mb-4" data-testid="edit-customer">
        <div className="font-heading font-black text-xl mb-2">CUSTOMER</div>
        <div>
          <label className="label-mono">Customer Name</label>
          <Autocomplete
            value={customer.name} testid="edit-input-customer-name"
            onChange={(v) => setCustomer({ ...customer, name: v })}
            fetcher={fetchCustomers}
            onSelect={(item) => {
              const c = item.raw || {};
              setCustomer({
                name: c.name || customer.name, city: c.city || customer.city,
                phone: c.phone || customer.phone, agent: c.agent || customer.agent,
                agent_phone: customer.agent_phone,
              });
            }}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-mono">City</label>
            <input className="brut-input" data-testid="edit-input-customer-city"
                   value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} />
          </div>
          <div>
            <label className="label-mono">Phone</label>
            <input className="brut-input" data-testid="edit-input-customer-phone" inputMode="numeric"
                   value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-mono">Agent</label>
            <Autocomplete
              value={customer.agent} testid="edit-input-customer-agent"
              onChange={(v) => setCustomer({ ...customer, agent: v })}
              fetcher={fetchAgents}
              onSelect={(item) => {
                const a = item.raw || {};
                setCustomer({ ...customer, agent: a.name || customer.agent, agent_phone: a.phone || customer.agent_phone });
              }}
            />
          </div>
          <div>
            <label className="label-mono">Agent Phone</label>
            <input className="brut-input" data-testid="edit-input-agent-phone" inputMode="numeric"
                   value={customer.agent_phone} onChange={(e) => setCustomer({ ...customer, agent_phone: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="brut-card p-5 mb-4" data-testid="edit-items">
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading font-black text-xl">ITEMS ({items.length})</div>
          <button onClick={addItem} className="brut-btn brut-btn-dark !py-2 text-xs" data-testid="add-item-btn">
            <Plus size={14} strokeWidth={3} /> ADD ITEM
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#F4F4F0] border-b-2 border-black">
            <tr className="text-left text-xs uppercase">
              <th className="px-2 py-2">Design</th>
              <th className="px-2 py-2">Qty</th>
              <th className="px-2 py-2">Unit</th>
              <th className="px-2 py-2">Rate</th>
              <th className="px-2 py-2">Amount</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-b border-black/15" data-testid={`edit-item-${i}`}>
                <td className="px-2 py-2"><input className="brut-input !py-2 !px-2 text-sm" value={it.design_number} onChange={(e) => updateItem(i, "design_number", e.target.value)} data-testid={`edit-item-design-${i}`} /></td>
                <td className="px-2 py-2"><input className="brut-input !py-2 !px-2 text-sm w-20" inputMode="numeric" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} data-testid={`edit-item-qty-${i}`} /></td>
                <td className="px-2 py-2">
                  <select className="brut-input !py-2 !px-2 text-sm" value={it.unit || "PCS"} onChange={(e) => updateItem(i, "unit", e.target.value)} data-testid={`edit-item-unit-${i}`}>
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </td>
                <td className="px-2 py-2"><input className="brut-input !py-2 !px-2 text-sm w-24" inputMode="decimal" value={it.rate} onChange={(e) => updateItem(i, "rate", e.target.value)} data-testid={`edit-item-rate-${i}`} /></td>
                <td className="px-2 py-2 font-mono">₹{Math.round((Number(it.quantity) || 0) * (Number(it.rate) || 0))}</td>
                <td className="px-2 py-2 text-right">
                  <button onClick={() => removeItem(i)} className="text-[#E63946]" data-testid={`edit-item-remove-${i}`}>
                    <Trash2 size={14} strokeWidth={3} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="6" className="px-4 py-6 text-center text-sm text-[#666] uppercase">No items</td></tr>}
          </tbody>
          <tfoot className="border-t-2 border-black">
            <tr className="font-mono"><td colSpan="4">Subtotal</td><td>₹{Math.round(subtotal).toLocaleString("en-IN")}</td><td></td></tr>
            <tr className="font-mono"><td colSpan="4">GST @ 5%</td><td>₹{Math.round(gstAmt).toLocaleString("en-IN")}</td><td></td></tr>
            <tr className="font-heading font-black"><td colSpan="4">GRAND TOTAL</td><td>₹{Math.round(grand).toLocaleString("en-IN")}</td><td></td></tr>
          </tfoot>
        </table>
      </div>

      <div className="brut-card p-6 mb-6">
        <label className="label-mono">Remarks</label>
        <textarea className="brut-input min-h-[80px]" data-testid="edit-remarks"
                  value={remarks} onChange={(e) => setRemarks(e.target.value)} />
      </div>

      <div className="flex gap-3">
        <button onClick={() => navigate(-1)} className="brut-btn brut-btn-secondary" data-testid="edit-cancel-btn">CANCEL</button>
        <button onClick={save} disabled={saving} className="brut-btn brut-btn-primary ml-auto" data-testid="edit-save-btn">
          <Save size={16} strokeWidth={3} /> {saving ? "SAVING…" : "SAVE CHANGES"}
        </button>
      </div>
    </div>
  );
}
